import Vue from "vue";
import Vuex, { ActionContext } from "vuex";
import createPersistedState from "vuex-persistedstate";
import axios from "axios";
//modules
import authentication from "./modules/authentication";
import inventoryState from "./modules/inventoryState";
import orderQuoteClone from "./modules/orderQuoteClone";
import purchasingState from "./modules/purchasingState";
import globalState from "./modules/globalState";
import systemCategories from "./modules/systemCategories";
import shippingDialog from "./modules/shippingDialog";
import ezeepHealth from "./modules/ezeepHealth";

Vue.use(Vuex);

// Define root state type for TypeScript
interface RootState {
	authentication?: {
		user?: any;
		status?: string;
		redirectUri?: string | null;
	};
	[key: string]: any;
}

export default new Vuex.Store({
	modules: {
		authentication,
		inventoryState,
		orderQuoteClone,
		purchasingState,
		globalState,
		systemCategories,
		shippingDialog,
		ezeepHealth,
	},
	actions: {
		async findOrderByPickTicket(context: ActionContext<any, RootState>, pickTicketId: number) {
			try {
				// Use REST API endpoint to find order by pick ticket
				const response = await axios.get(`/api/pick-tickets/${pickTicketId}/order`);
				
				if (response.data) {
					const order = response.data;
					
					// Check if order is valid for label creation
					if (order.isCancelled) {
						throw new Error('This order has been cancelled');
					}

					// Check if pick ticket already has a label
					if (order.hasLabel && order.trackingNumber) {
						throw new Error(`Pick ticket #${order.pickTicketId} already has tracking number: ${order.trackingNumber}`);
					}

					// Return the order data
					return {
						salesOrderId: order.salesOrderId,
						orderNumber: order.orderNumber,
						customerId: order.customerId,
						customerName: order.customerName,
						status: order.status,
						pickTicketId: order.pickTicketId,
						hasLabel: order.hasLabel,
						trackingNumber: order.trackingNumber,
						shippingDate: order.shippingDate
					};
				}
				
				// No order found for this pick ticket
				throw new Error(`No order found for pick ticket #${pickTicketId}. The pick ticket may not exist or has no associated orders.`);
			} catch (error: any) {
				if (error.response?.status === 404) {
					throw new Error(`No order found for pick ticket #${pickTicketId}. The pick ticket may not exist or has no associated orders.`);
				}
				console.error('Failed to find order by pick ticket:', error);
				throw error;
			}
		},
		
		async checkLabelPermission(context: ActionContext<any, RootState>) {
			// Check user permissions for label creation
			const user = context.rootState.authentication?.user;
			if (!user) return false;

			// Check for specific permission or admin status
			const permissions = user.permissions || [];
			const isAdmin = user.isAdmin || user.role === 'Admin';

			// Check if user is shipper role
			const isShipper = user.roles && user.roles.some((role: any) => role.name === 'shipper');

			// Check for shipper-specific permissions
			const hasShipperCreatePermission = permissions.includes('Shipper_Shipping_CreateLabel') ||
				user.roles?.some((role: any) =>
					role.permissions?.some((perm: any) => perm.name === 'Shipper_Shipping_CreateLabel')
				);

			return permissions.includes('SHIPPING_LABEL_CREATE') ||
			       permissions.includes('SHIPPING_ALL') ||
			       permissions.includes('ADMIN') ||
			       isAdmin ||
			       isShipper ||
			       hasShipperCreatePermission;
		}
	},
	plugins: [
		createPersistedState({
			storage: window.localStorage,
		}),
	],
});
